import React from 'react';
import ReactDOM from 'react-dom/client';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assertAndContinue} from 'Asserts';
import {SequencePrimerCollection} from 'BioJS';
import * as biojs from 'BioJS';
import {Notation} from 'biojs/BioConstants';
import BufferTabView from 'buffers/BufferTabView';
import {AttachPrimerOptions} from 'constants/AttachPrimerConstants';
import {PrimerPreferences} from 'constants/PrimerConstants';
import {SequenceServerResponse} from 'constants/SequenceConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {FindPrimersOptions} from 'molbio/find-primers/FindPrimersConstants';
import {FindPrimersResultsFetchResponse} from 'molbio/find-primers/FindPrimersResultsCollection';
import FindPrimersTabContent from 'molbio/find-primers/FindPrimersTabContent';
import {FindPrimersTabState} from 'molbio/find-primers/FindPrimersTabState';
import {setMisprimePreferences} from 'molbio/misprime/MisprimeUtils';
import notifications from 'notifications';
import preferences from 'Preferences';
import {renderToDOM, unmountRootsAsync} from 'redux-stores/ReduxDOMRender';
import * as net from 'util/Net';

class ReactFindPrimersTabView extends BufferTabView<FindPrimersTabState> {
  reactRoot: ReactDOM.Root | null = null;

  override initialize(): void {
    const sequence = this.model.get('sequence');

    this.listenTo(this.model, 'change', () => this.render());
    this.listenTo(sequence.selection(), 'change', () => this.render());
    this.listenTo(
      sequence._sequence(),
      'change:bases change:circular change:startIndex change:locked',
      () => {
        // Results may no longer be accurate so force user to trigger new search.
        this.model.set({showResults: false});
        this.render();
      }
    );
    this.listenTo(sequence, 'change:currentVersion', () => this.render());

    // Changes to results collection.
    this.listenTo(this.model.get('results'), 'sync', () => this.render());
    this.listenTo(this.model.get('results'), 'change:disabled', () => this.render());
    const sequencePrimers = this.model.get('sequence').get('_sequence').sequencePrimers();
    const listenToBatch = this.listenToBatch(sequencePrimers, () => {
      this.render();
    });
    this.listenTo(sequencePrimers, 'add remove sync', () => {
      listenToBatch();
    });

    // Changes to search collections.
    this.listenTo(this.model.get('searchExistingBases'), 'sync', () => this.render());
    this.listenTo(this.model.get('searchExisting'), 'sync', () => this.render());
    this.listenTo(this.model.get('searchExisting'), 'change', () => this.render());
  }

  override removeAsync(callback: () => void): this {
    unmountRootsAsync([this.reactRoot], () => {
      this.reactRoot = null;
      super.remove();
      callback();
    });
    return this;
  }

  /**
   * Create a new oligo sequence entity along with primers that bind it to the analyzed sequence.
   */
  async createOligoAndSavePrimers(): Promise<void> {
    const sequence = this.model.get('sequence');
    const sequencePrimers = sequence._sequence().sequencePrimers();
    const newPrimers = this.model.getNewPrimers();

    const name = this.model.get('primerName');
    const folderId = this.model.get('selectedSaveLibrary')!;
    const oligo: SequenceServerResponse = await net.postJSONPromise({
      url: '/1/api/oligos/',
      data: {
        name,
        bases: this.model.get('searchBases'),
        color: this.model.get('color'),
        folderId,
        nucleotideType: this.model.get('nucleotideType'),
        notation: Notation.NATURAL_BASES,
      },
    });
    // Then save the sequence primers
    const primersToSave = new biojs.SequencePrimerCollection(
      newPrimers.map((primer) => {
        const primerClone = primer.clone();
        primerClone.set({name, folder_api_id: folderId, primer_api_id: oligo.id});
        return primerClone;
      }),
      {sequenceID: sequence.id}
    );
    // @ts-expect-error
    const {primers, message} = await Backbone.sync('create', primersToSave, {fileUpdate: true});
    for (const primer of newPrimers) {
      primer.set({disabled: true});
    }
    sequencePrimers.batchModify(function (this: SequencePrimerCollection) {
      // @ts-expect-error
      return this.add(primers, {parse: true});
    });
    sequence.updateVersions();
    notifications.success(message);
    analytics.track('primer', 'added_primers', {
      product: ANALYTICS_MODULE.MOLBIO,
      source: 'find-primers',
      sequence_api_id: sequence.id,
      primer_count: newPrimers.length,
    });
  }

  async searchOligos(): Promise<void> {
    this.model.removePrimers();
    await this.model.get('searchExisting')!.fetch({
      data: {
        name: this.model.get('searchName'),
        bases: this.model.get('searchBases'),
        nucleotideType: this.model.get('nucleotideType'),
      },
    });
  }

  async findPrimers(): Promise<void> {
    if (this.model.findPrimersDisabled()) {
      return;
    }
    this.model.set('showResults', false);

    analytics.track('primer', 'find_binding_sites_click', {
      product: ANALYTICS_MODULE.MOLBIO,
      sequence_id: this.model.get('sequence').get('id'),
    });
    this.model.removePrimers();
    setMisprimePreferences(this.model.getMisprimingState());
    const data: AttachPrimerOptions = {
      min_matching_bases: this.model.get('minMatchingBases')!,
      min_temp: this.model.get('minMeltingTemp')!,
      max_temp: this.model.get('maxMeltingTemp')!,
      min_base_index: this.model.get('minBaseIndex'),
      max_base_index: this.model.get('maxBaseIndex'),
      exact_match: this.model.get('exactMatch')!,
      unique_match: this.model.get('uniqueMatch')!,
      max_total_mismatches: this.model.get('maxTotalMismatches')!,
      max_mismatches: this.model.get('maxMismatches'),
      dnaTmPreferences: preferences.get(PrimerPreferences.MELTING_TEMP_DNA),
      rnaTmPreferences: preferences.get(PrimerPreferences.MELTING_TEMP_RNA),
    };
    const searchExistingCollection = this.model.get('searchExisting')!;
    const findType = this.model.get('findType');
    if (findType === FindPrimersOptions.FOLDERS) {
      const folderIds = this.model.prepareSelectedLibrariesForFind();
      FolderDefaults.setList(FolderDefaultsKey.FIND_OLIGO, folderIds);
      data.folderIds = folderIds;
      data.nucleotideType = this.model.get('nucleotideType');
    } else if (findType === FindPrimersOptions.SINGLE) {
      const primer = searchExistingCollection.findWhere({selected: true});
      data.oligoId = primer.get('primer_id');
    } else if (findType === FindPrimersOptions.BASES) {
      // Kick off a search to see if there are any existing oligos with the exact bases being searched for.
      await this.model.get('searchExistingBases')!.fetch({
        data: {bases: this.model.get('searchBases'), nucleotideType: this.model.get('nucleotideType')},
      });
      data.bases = this.model.get('searchBases');
      data.nucleotideType = this.model.get('nucleotideType');
    } else if (findType === FindPrimersOptions.WORKLIST) {
      const selectedWorklistIds = this.model.prepareSelectedLibrariesForFind();
      assertAndContinue(
        selectedWorklistIds.length === 1,
        `Expected exactly one selected worklist in find primers, got ${selectedWorklistIds.length}.`
      );
      if (!selectedWorklistIds.length) {
        return;
      }
      data.worklistId = selectedWorklistIds[0];
      data.nucleotideType = this.model.get('nucleotideType');
    } else if (findType === FindPrimersOptions.TAG_SCHEMA) {
      data.tagSchemaId = this.model.get('tagSchemaId')!;
      data.nucleotideType = this.model.get('nucleotideType');
    }
    let res: FindPrimersResultsFetchResponse[string];
    try {
      res = (await this.model.get('results')!.fetch({data}))!;
    } catch (e) {
      analytics.track('primer', 'find_primers_fail', {
        product: ANALYTICS_MODULE.MOLBIO,
        error: notifications.getFriendlyError(e),
      });
      throw e;
    }
    analytics.track('primer', 'found_primers', {
      product: ANALYTICS_MODULE.MOLBIO,
      count: this.model.get('results')!.length,
      more_results: res.more_results,
      lower_tm_results: res.lower_tm_results,
      higher_tm_results: res.higher_tm_results,
      sequence_id: this.model.get('sequence').id,
    });
    for (const primer of this.model.get('results')!.models) {
      if (this.model.get('findType') === FindPrimersOptions.BASES) {
        primer.set({color: this.model.get('color')});
      }
      const samePrimer = this.model
        .get('sequence')
        ._sequence()
        .sequencePrimers()
        .findWhere({
          start: primer.get('start'),
          end: primer.get('end'),
          primer_id: primer.get('primer_id'),
        });
      if (samePrimer != null) {
        primer.set({disabled: true});
      }
    }
    this.model.set({
      moreResults: res.more_results,
      lowerTmResults: res.lower_tm_results,
      higherTmResults: res.higher_tm_results,
      maxResults: res.max_results,
    });

    // Ensure that a change event is emitted even if `showResults` is already true.
    this.model.unset('showResults', {silent: true}).set({showResults: true});
  }

  override render(): this {
    this.reactRoot = renderToDOM(
      <FindPrimersTabContent
        createOligoAndSavePrimers={this.createOligoAndSavePrimers.bind(this)}
        onFindPrimers={this.findPrimers.bind(this)}
        findPrimersTabState={this.model}
        searchOligos={this.searchOligos.bind(this)}
      />,
      this.el,
      this.reactRoot
    );
    return this;
  }
}
ReactFindPrimersTabView.prototype.className = 'find-primers-view buffer-tab';
export default ReactFindPrimersTabView;
